import React from 'react';

// The Zeevogo "globe-route" mark: a ring (globe) with faint meridian/parallel
// lines, a dotted diagonal route at -35deg, an amber arrowhead mid-route, and
// two station dots. Base geometry is authored at 120x120 and scaled via
// transform so it stays crisp at any size and stays theme-reactive (ink/fill
// default to the CSS vars swapped by [data-theme]).
export const LogoMark = ({
  size = 36,
  ink = 'var(--zg-logo-ink)',
  fill = 'var(--zg-logo-fill)',
  dotFill,
  floaty = false,
  extraRing = false,
  opacity = 1,
  style,
}) => {
  const scale = size / 120;
  const resolvedDotFill = dotFill ?? fill;

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        width: size,
        height: size,
        flexShrink: 0,
        verticalAlign: 'middle',
        opacity,
        animation: floaty ? 'floaty 4.5s ease-in-out infinite' : undefined,
        ...style,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 120,
          height: 120,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          display: 'block',
        }}
      >
        <span
          style={{
            position: 'absolute',
            inset: 14,
            background: fill,
            border: `9px solid ${ink}`,
            borderRadius: '50%',
            display: 'block',
          }}
        />
        <span style={{ position: 'absolute', inset: 23, borderRadius: '50%', overflow: 'hidden', display: 'block' }}>
          <span
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 0,
              borderTop: `2px solid ${ink}`,
              opacity: 0.3,
              display: 'block',
            }}
          />
          <span
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 0,
              borderLeft: `2px solid ${ink}`,
              opacity: 0.3,
              display: 'block',
            }}
          />
          {extraRing && (
            <span
              style={{
                position: 'absolute',
                inset: 0,
                margin: 'auto',
                width: '50%',
                height: '100%',
                border: `1px solid ${ink}`,
                borderRadius: '50%',
                opacity: 0.3,
                display: 'block',
              }}
            />
          )}
        </span>
        <span
          style={{
            position: 'absolute',
            left: 16,
            top: 58,
            width: 88,
            height: 0,
            borderTop: `3px dotted ${ink}`,
            transform: 'rotate(-35deg)',
            display: 'block',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: 44,
            top: 44,
            width: 32,
            height: 32,
            background: '#e08e0b',
            clipPath: 'polygon(0 0,100% 50%,0 100%,26% 50%)',
            transform: 'rotate(-35deg)',
            display: 'block',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: 13,
            bottom: 22,
            width: 24,
            height: 24,
            background: resolvedDotFill,
            borderRadius: '50%',
            border: `4px solid ${ink}`,
            display: 'block',
          }}
        />
        <span
          style={{
            position: 'absolute',
            right: 13,
            top: 22,
            width: 24,
            height: 24,
            background: '#e08e0b',
            borderRadius: '50%',
            border: `4px solid ${ink}`,
            display: 'block',
          }}
        />
      </span>
    </span>
  );
};

export const Wordmark = ({ size = 28, color = 'var(--zg-ink)', style }) => (
  <span
    style={{
      fontFamily: "'Bricolage Grotesque', sans-serif",
      fontWeight: 800,
      letterSpacing: '-0.03em',
      fontSize: size,
      color,
      ...style,
    }}
  >
    zeevo<span style={{ color: '#e08e0b' }}>go</span>
  </span>
);

// Convenience combo used in header/footer/admin chrome.
const Logo = ({ markSize = 36, wordmarkSize = 28, ink, fill, floaty = false, onClick, className }) => (
  <span
    onClick={onClick}
    className={className}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: onClick ? 'pointer' : undefined }}
  >
    <LogoMark size={markSize} ink={ink} fill={fill} floaty={floaty} />
    <Wordmark size={wordmarkSize} color={ink} />
  </span>
);

export default Logo;
